<template>
  <div class="navbar" id="topbar">
    <!-- ==================== DESKTOP: single compact row ==================== -->
    <div
      v-if="$vuetify.breakpoint.mdAndUp"
      class="navbar-desktop"
      :style="{ direction: $vuetify.rtl ? 'rtl' : 'ltr' }"
    >
      <!-- Left: sidebar toggle + compact logo -->
      <div class="nd-left">
        <Icon
          iconClass="pointer nd-menu-icon"
          icon="menuIcon"
          :large="true"
          color="grey darken-2"
          @click.native="sidebar()"
        />
        <div class="nd-logo pointer ms-2" @click="changeRoute('/')">
          <Logo :slogan="true" size="xs" />
        </div>
      </div>

      <!-- Center-left: search -->
      <div class="nd-search">
        <search-input @close="search = false" :focused="search" />
      </div>

      <!-- Center: nav buttons -->
      <div class="nd-nav">
        <v-btn
          @click="changeRoute('/dashboard')"
          text
          small
          :class="[
            'nd-nav-btn',
            $route.path === '/dashboard' ? 'nd-nav-btn--active' : '',
          ]"
          :color="!$vuetify.theme.dark ? 'switchq' : 'switchqSub lighten-3'"
        >
          <v-icon small class="me-1">mdi-view-dashboard</v-icon>
        </v-btn>
        <v-btn
          @click="changeRoute('/devices')"
          text
          small
          :class="[
            'nd-nav-btn',
            $route.path === '/devices' ? 'nd-nav-btn--active' : '',
          ]"
          :color="!$vuetify.theme.dark ? 'switchq' : 'switchqSub lighten-3'"
        >
          {{ $t("links.all-devices") }}
        </v-btn>
        <v-btn
          @click="changeRoute('/groups')"
          text
          small
          :class="[
            'nd-nav-btn',
            $route.path === '/groups' ? 'nd-nav-btn--active' : '',
          ]"
          :color="!$vuetify.theme.dark ? 'switchq' : 'switchqSub lighten-3'"
        >
          {{ $t("links.groups") }}
        </v-btn>
        <v-btn
          @click="changeRoute('/map')"
          text
          small
          :class="[
            'nd-nav-btn',
            $route.path === '/map' ? 'nd-nav-btn--active' : '',
          ]"
          :color="!$vuetify.theme.dark ? 'switchq' : 'switchqSub lighten-3'"
        >
          <!-- <v-icon small class="me-1">mdi-map-outline</v-icon> -->
          {{ $t("links.map") }}
        </v-btn>
        <v-btn
          v-if="!isHome"
          text
          small
          class="nd-nav-btn"
          :color="!$vuetify.theme.dark ? 'switchq' : 'switchqSub lighten-3'"
          @click="$router.go(-1)"
        >
          <v-icon small>mdi-arrow-left</v-icon>
        </v-btn>
      </div>

      <!-- Left (RTL end): time + notifications + user pill -->
      <div class="nd-right">
        <div
          v-if="$vuetify.breakpoint.lgAndUp"
          class="nd-time grey--text text--darken-2"
        >
          <span class="nd-time-clock">{{ time.format("HH:mm") }}</span>
          <span class="nd-time-divider mx-1">|</span>
          <span>{{ time.format("DD/MM/YYYY") }}</span>
        </div>
        <notifications
          v-if="logged"
          shrink
          class="pa-0 ma-0 topbar-btn"
          :devices="devices"
        />
        <div class="nd-user ms-2" @click="changeRoute('/profile')">
          <div class="nd-user-info me-2">
            <div class="nd-user-business">
              {{ business.custom ? business.title : business.name }}
            </div>
            <div class="nd-user-name">{{ username }}</div>
          </div>
          <div class="nd-user-avatar">{{ userInitials }}</div>
        </div>
      </div>
    </div>
    <!-- ==================== /DESKTOP ==================== -->

    <!-- ==================== MOBILE: original two-row structure ==================== -->
    <template v-if="$vuetify.breakpoint.smAndDown">
      <div class="navbar-top">
        <div class="navbar-top-side">
          <Icon
            iconClass="pointer"
            icon="menuIcon"
            :xlarge="true"
            color="grey darken-2"
            @click.native="sidebar()"
          />
          <v-icon class="mobile-search-shortcut" @click="search = true">
            mdi-magnify
          </v-icon>
        </div>
        <div class="navbar-top-logo pointer" @click="changeRoute('/')">
          <Logo :slogan="false" :size="$vuetify.breakpoint.name" />
        </div>
        <div class="navbar-top-side">
          <notifications
            v-if="logged"
            shrink
            class="pa-0 ma-0 topbar-btn"
            :devices="devices"
          ></notifications>
        </div>
      </div>
      <div
        v-if="showBottom && swipDisplay && search"
        class="navbar-bottom small-bar"
      >
        <v-layout row wrap justify-space-between align-center>
          <div v-if="search" class="d-flex w-100 justify-space-between">
            <search-input @close="search = false" :focused="search" />
            <v-btn
              color="info"
              class="me-n5"
              text
              height="auto"
              @click="closeSearch()"
            >
              <Icon icon="mdiClose" iconClass="" />
            </v-btn>
          </div>
        </v-layout>
      </div>
    </template>
    <!-- ==================== /MOBILE ==================== -->
  </div>
</template>

<script>
import Notifications from "./notifications";
import Logo from "@/components/app_parts/logo.vue";
import SearchInput from "./SearchInput.vue";
export default {
  name: "TopBar",
  components: { Notifications, Logo, SearchInput },
  props: ["devices", "swipDisplay"],
  data() {
    return {
      clipped: true,
      openedMenu: null,
      title: "SwitchQ",
      logged: true,
      locale: null,
      search: false,
    };
  },
  computed: {
    backRoute() {
      let back = this.$route.path.split("/");
      back.pop();
      back = back.join("/");
      return back;
    },
    showBottom() {
      return true;
    },
    isHome() {
      return ["/dashboard", "/devices"].includes(this.$route.path);
    },
    path() {
      return this.$route.path;
    },
    time() {
      return this.$store.getters["general/time"];
    },
    username() {
      return this.$store.getters["user/name"];
    },
    business() {
      return this.$store.getters["business/business"];
    },
    userInitials() {
      const name = this.username || "";
      return name
        .split(" ")
        .map((w) => (w[0] || "").toUpperCase())
        .join("")
        .slice(0, 2);
    },
  },
  created() {
    this.$store.dispatch("general/startTimeInterval");
  },
  watch: {
    path: {
      handler() {
        this.closeSearch();
      },
      deep: true,
    },
  },
  mounted() {},
  methods: {
    changeRoute(path) {
      if (this.$router.history.current.path != path) {
        this.$router.push(path);
      }
    },
    closeSearch() {
      this.$store.dispatch("devices/setSearchDevice", "");
      this.search = false;
    },
    openMenu() {
      document.getElementById("menuBtn").click();
    },
    sidebar() {
      this.$store.dispatch("general/toggleSideMenu");
    },
  },
};
</script>

<style scoped lang="scss">
/* ===================== DESKTOP NAVBAR ===================== */
.navbar-desktop {
  background-color: #fff;
  border-bottom: 1px solid rgb(224, 227, 231);
  box-shadow: 0 1px 8px rgba(17, 24, 39, 0.06);
  height: 64px;
  padding: 0 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

/* Left: menu + logo */
.nd-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;

  .nd-menu-icon {
    cursor: pointer;
  }

  .nd-logo {
    display: flex;
    align-items: center;
    /* Constrain the logo to be small */
    :deep(.logo-container) {
      padding: 0;
    }
    :deep(.logo-container img) {
      max-height: 36px !important;
      width: auto;
      max-width: 36px;
    }
    :deep(#logo-text-name) {
      font-size: 1.15rem !important;
      line-height: 1;
    }
    :deep(#logo-text) {
      display: flex;
      flex-direction: column;
    }
    /* Hide subtitle, show only SwitchQ name */
    :deep(#logo-text-slogan) {
      display: none !important;
    }
  }
}

/* Search */
.nd-search {
  width: 220px;
  flex-shrink: 0;
}

/* Nav */
.nd-nav {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 2px;

  .nd-nav-btn {
    letter-spacing: 0;
    font-size: 0.875rem;
    text-transform: none;
    border-radius: 6px;
    position: relative;

    &--active::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 8px;
      right: 8px;
      height: 2px;
      border-radius: 2px;
      background: currentColor;
    }
  }
}

/* Left (RTL end) */
.nd-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  direction: ltr;
}

.nd-time {
  display: flex;
  align-items: center;
  font-size: 12px;
  white-space: nowrap;
  gap: 2px;

  .nd-time-divider {
    color: #cecece;
  }
  .nd-time-clock {
    min-width: 36px;
  }
}

.nd-user {
  display: flex;
  align-items: center;
  cursor: pointer;
  direction: ltr;
  gap: 6px;

  .nd-user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #7b2cbf;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }

  .nd-user-info {
    display: flex;
    flex-direction: column;

    .nd-user-business {
      font-size: 13px;
      font-weight: 700;
      line-height: 1.2;
      color: #222;
      white-space: nowrap;
      text-align: right;
    }
    .nd-user-name {
      font-size: 11px;
      color: #777;
      line-height: 1.2;
      white-space: nowrap;
      text-align: right;
    }
  }
}

/* ===================== MOBILE NAVBAR (original) ===================== */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1100;
  box-sizing: border-box;
  background-color: #fff;
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  // Force a separate GPU compositing layer on iOS Safari so the topbar
  // paints reliably at page load regardless of page-level stacking context.
  will-change: transform;
  isolation: isolate;
  contain: layout paint;

  .navbar-top {
    position: relative;
    z-index: 1;
    background-color: #fff;
    border-bottom: 1px solid rgb(224, 227, 231);
    box-sizing: border-box;
    padding: calc(8px + var(--sq-safe-area-top, 0px)) 16px 8px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    height: var(--sq-mobile-topbar-height, 60px);
    min-height: var(--sq-mobile-topbar-height, 60px);

    .navbar-top-side {
      min-width: 86px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 18px;

      &:last-child {
        justify-content: flex-end;
      }
    }
  }

  .navbar-bottom {
    min-height: 40px;
    background-color: rgb(242, 246, 249);
    border-bottom: 1px solid rgb(224, 227, 231);
    padding: 6px 16px;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
  }
}

.mobile-search-shortcut {
  color: #424242;
  font-size: 34px;
}

@media screen and (min-width: 960px) {
  .mobile-search-shortcut {
    display: none !important;
  }
}

/* .v-btn letter-spacing fix inside bottom */
.nd-nav-btn {
  letter-spacing: 0;
}
</style>
